import React, { useEffect, useCallback, useState, useContext, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdminNav from '../../components/admin/AdminNav';
import { GroupContext } from '../../context/GroupContext';
import api from '../../utils/api';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SupplierInfoModal from '../../components/common/SupplierInfoModal';

const EPSILON = 0.001;

export default function AdminDashboard() {
  const { groups, fetchGroups, replaceGroup, removeGroup } = useContext(GroupContext) || {
    groups: [],
    fetchGroups: async () => [],
    replaceGroup: () => {},
    removeGroup: () => {},
  };

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierForModal, setSupplierForModal] = useState(null);

  const groupsPerPage = 10;
  const navigate = useNavigate();

  // Role-based user
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = storedUser?.role;

  const load = useCallback(async () => {
    try {
      // Superadmin sees all groups
      await fetchGroups({});
    } catch (err) {
      console.error('Failed to fetch groups:', err);
      toast.error('Failed to load groups');
    }
  }, [fetchGroups]);

  useEffect(() => {
    // Only superadmin can access
    if (userRole !== 'superadmin') {
      navigate('/login');
    } else {
      void load();
    }
  }, [userRole, navigate, load]);

  const effectiveStatusFor = useCallback((group) => {
    const rem = Number(group.remainingCost ?? group.totalCost ?? 0);
    if (group.status === 'closed') return 'closed';
    if (rem <= EPSILON) return 'full';
    return 'open';
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    try {
      const updatedGroup = await api.patch(`/api/groups/${id}/status`, { status: newStatus });
      replaceGroup(updatedGroup);
      toast.success(`Group stored status updated to ${newStatus}`);
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update status');
    }
  };

  const openDeleteModal = (group) => {
    setGroupToDelete(group);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setGroupToDelete(null);
    setDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    if (!groupToDelete) return;
    try {
      await api.delete(`/api/groups/${groupToDelete._id}`);
      toast.success(`Group ${groupToDelete.name || ''} deleted`);
      removeGroup(groupToDelete._id);
      closeDeleteModal();
    } catch (err) {
      console.error('Failed to delete group:', err);
      toast.error('Failed to delete group');
      closeDeleteModal();
    }
  };

  const openSupplierModal = (supplier) => {
    if (!supplier) return;
    setSupplierForModal(supplier);
    setSupplierModalOpen(true);
  };

  const closeSupplierModal = () => {
    setSupplierModalOpen(false);
    setSupplierForModal(null);
  };

  const getStatusClasses = (status) =>
    ({
      open: 'bg-green-500 text-white',
      full: 'bg-yellow-500 text-black',
      closed: 'bg-gray-500 text-white',
    }[status] || 'bg-gray-300 text-black');

  const sortByKey = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const sortedGroups = useMemo(() => {
    if (!sortConfig.key) return groups;

    return [...groups].sort((a, b) => {
      let aVal, bVal;

      if (sortConfig.key === 'effectiveStatus') {
        aVal = effectiveStatusFor(a);
        bVal = effectiveStatusFor(b);
      } else if (sortConfig.key === 'createdBy') {
        aVal = a.createdBy?.username || '';
        bVal = b.createdBy?.username || '';
      } else if (sortConfig.key === 'supplierId') {
        aVal = a.supplierId?.name || '';
        bVal = b.supplierId?.name || '';
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }

      if (['dateCreated', 'dateClosed', 'createdAt', 'updatedAt'].includes(sortConfig.key)) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      aVal = (aVal || '').toString().toLowerCase();
      bVal = (bVal || '').toString().toLowerCase();

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [groups, sortConfig, effectiveStatusFor]);

  const indexOfLastGroup = currentPage * groupsPerPage;
  const indexOfFirstGroup = indexOfLastGroup - groupsPerPage;
  const currentGroups = sortedGroups.slice(indexOfFirstGroup, indexOfLastGroup);
  const totalPages = Math.ceil(sortedGroups.length / groupsPerPage);

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-7xl mx-auto bg-white shadow p-6 rounded-md">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <Link
            to="/admin/create-group"
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            + Create New Group
          </Link>
        </div>

        {sortedGroups.length === 0 ? (
          <p>No groups found.</p>
        ) : (
          <>
            <table className="min-w-full bg-white border">
              <thead className="bg-gray-200">
                <tr>
                  <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('name')}>
                    Group Name{renderSortArrow('name')}
                  </th>
                  <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('supplierId')}>
                    Supplier{renderSortArrow('supplierId')}
                  </th>
                  <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('state')}>
                    State{renderSortArrow('state')}
                  </th>
                  <th
                    className="p-3 text-left cursor-pointer"
                    onClick={() => sortByKey('effectiveStatus')}
                  >
                    Status (effective){renderSortArrow('effectiveStatus')}
                  </th>
                  <th
                    className="p-3 text-left cursor-pointer"
                    onClick={() => sortByKey('createdBy')}
                  >
                    Created By{renderSortArrow('createdBy')}
                  </th>
                  <th
                    className="p-3 text-left cursor-pointer"
                    onClick={() => sortByKey('createdAt')}
                  >
                    Created At{renderSortArrow('createdAt')}
                  </th>
                  <th
                    className="p-3 text-left cursor-pointer"
                    onClick={() => sortByKey('updatedAt')}
                  >
                    Updated At{renderSortArrow('updatedAt')}
                  </th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentGroups.map((group) => {
                  const effStatus = effectiveStatusFor(group);
                  return (
                    <tr key={group._id} className="border-b hover:bg-gray-50">
                      <td className="p-3">{group.name}</td>
                      <td className="p-3">
                        {group.supplierId ? (
                          <button
                            onClick={() => openSupplierModal(group.supplierId)}
                            className="text-blue-600 hover:underline"
                          >
                            {group.supplierId.name}
                          </button>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="p-3">{group.state}</td>
                      <td className="p-3 flex flex-col gap-1">
                        <div>
                          <span
                            className={`inline-block px-2 py-1 text-sm rounded ${getStatusClasses(
                              effStatus
                            )}`}
                          >
                            {effStatus}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600">
                          Stored:{' '}
                          <select
                            value={group.status}
                            onChange={(e) => handleStatusChange(group._id, e.target.value)}
                            className={`border rounded px-1 py-0.5 ${getStatusClasses(
                              group.status
                            )}`}
                          >
                            <option value="open">Open</option>
                            <option value="full">Full</option>
                            <option value="closed">Closed</option>
                          </select>
                        </div>
                      </td>
                      <td className="p-3">{group.createdBy?.username || 'N/A'}</td>
                      <td className="p-3">
                        {group.createdAt ? new Date(group.createdAt).toLocaleString() : '—'}
                      </td>
                      <td className="p-3">
                        {group.updatedAt ? new Date(group.updatedAt).toLocaleString() : '—'}
                      </td>
                      <td className="p-3 space-x-2">
                        <Link
                          to={`/admin/groups/view/${group._id}`}
                          className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                        >
                          View
                        </Link>
                        <Link
                          to={`/admin/groups/edit/${group._id}`}
                          className="bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => openDeleteModal(group)}
                          className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="flex justify-center mt-6 space-x-2">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-1 rounded ${
                    currentPage === i + 1
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </>
        )}

        <ConfirmationModal
          isOpen={deleteModalOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          message={`Are you sure you want to delete group "${groupToDelete?.name}"? This action cannot be undone.`}
        />

        <SupplierInfoModal
          supplier={supplierForModal}
          isOpen={supplierModalOpen}
          onClose={closeSupplierModal}
        />

        <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      </div>
    </div>
  );
}
